// Payouts → Reconciliation — does the document still equal the supply it states?
//
// THE NORTH STAR HAD NO INVARIANT. Everything the share oracle checks (O1–O5)
// lives on the COLLECTION plane — revenue_share_lines, rs_processed_invoices,
// payout_statements — which the founder ruled on 24 Sep is the engine's working
// and not what we owe. The plane that actually produces the money (presence →
// device_day_eligibility → earnings-fold → self_billed_invoices) had nothing
// asserting anything. A media owner could go uninvoiced, or be invoiced for the
// wrong amount, and nothing anywhere would have noticed.
//
// This screen is that missing instrument, and it is also the one you watch
// while the money-cell migration runs — which is why the reminder bar in the
// shell links here rather than to a ticket.
//
// EVERY CHECK IS READ-ONLY AND PURE SQL. Nothing here can move a rupee.

import React from 'react';
import { PageHead, DataTable, Loading, ErrorState, Pill, Button } from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';
import { inr, BASE, shaped, fmtDay } from './lib';
import Explainer from './Explainer';
import s from './Payouts.module.css';

// The day the money-cell migration was agreed. This is a START MARKER for
// elapsed time, NOT a gate — the gate is the predicate below it. "It has been N
// days, flip it" is a date standing in for a predicate, which is the thing the
// loss-card clamp was removed for.
export const FLIP_DECIDED_ON = '2026-09-24';

export const daysSince = (iso, today = new Date()) => {
  const a = Date.parse(`${iso}T00:00:00Z`);
  const b = Date.parse(`${today.toISOString().slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(a) || Number.isNaN(b) ? null : Math.max(0, Math.round((b - a) / 86400000));
};

// The three things that must ALL be true before the read may be flipped from
// raw batches to the money cell. Stated here, in one place, so the bar and this
// screen can never describe readiness differently.
export const flipGates = (flip) => [
  // Each gate carries BOTH voices. `label` is the positive assertion the screen
  // lists; `pending` is what the bar says while it is unmet — prefixing "Not
  // ready:" to a positive assertion produced "Not ready: the money cell exists
  // and is being written", which reads as though it did.
  { id: 'exists', label: 'The money cell exists and is being written',
    pending: 'the money cell has not been built yet',
    met: !!flip?.cells_exist,
    why: 'V364 creates it; the writer fills it. Until then there is nothing to compare and nothing to flip to.' },
  { id: 'identical', label: 'The fold returns byte-identical months from cells and from raw',
    pending: 'the fold has not yet been proved identical from cells and from raw',
    met: !!flip?.identical,
    why: 'Every field of every judged month, zero tolerance. The fold is the source of the money, so "close" is not a result.' },
  { id: 'closed', label: 'It survived a real month close',
    pending: 'no month has closed since the shadow began',
    met: !!flip?.survived_close,
    why: 'A month close is when a document gets filed. Shadow agreement inside a month proves less than agreement across the act that freezes one.' },
];

export default function Recon() {
  const { data, error, loading, reload } = useApi(
    () => api.get(`${BASE}/recon`).then((r) => shaped(r.data, 'checks')), []);
  // R5 IS FETCHED SEPARATELY AND DELIBERATELY. It re-folds each filed month from
  // source, which costs ~470ms per month against ~30ms for all of R1-R4 — so the
  // cheap answer renders immediately and this fills in behind it. A single
  // request would make the fast checks wait for the slow one.
  const drift = useApi(() => api.get(`${BASE}/recon/drift`).then((r) => shaped(r.data, 'drift')), []);

  const head = (
    <PageHead
      title="Reconciliation"
      sub="Does each filed document still equal the supply it states? These checks read the invoicing plane — the one the money actually comes from — and can never change a number."
      actions={<Button onClick={reload}>Refresh</Button>}
    />
  );

  if (loading) return <div className={s.wrap}>{head}<Loading what="the reconciliation" /></div>;
  if (error) return <div className={s.wrap}>{head}<ErrorState error={error} onRetry={reload} /></div>;

  const { checks, tenants, documents, flip } = data;
  const breached = checks.filter((c) => c.state === 'breach');
  const d = drift.data?.drift;
  const gates = flipGates(flip);
  const met = gates.filter((g) => g.met).length;
  const elapsed = daysSince(FLIP_DECIDED_ON);

  return (
    <div className={s.wrap}>
      {head}

      <div className={breached.length ? s.badBanner : s.okBanner}>
        <b>{breached.length ? `${breached.length} of ${checks.length} checks BREACHED:` : `All ${checks.length} checks clean:`}</b>{' '}
        {breached.length
          ? breached.map((c) => c.detail).join(' · ')
          : 'every filed document agrees with its own annexure, no refusal has outlived its cause, the series is unique per supplier-month, and every correction names an original that exists.'}
      </div>

      {/* ── R5 ─────────────────────────────────────────────────────────────
          The only check on this plane whose two sides are independent. R1-R4
          compare the invoicing plane to ITSELF — a document against its own
          frozen annexure, a block against its own month. None of them can see
          the failure that matters most: the underlying data moving AFTER a
          document was filed. A late batch, a promoted presence verdict, a
          back-dated ownership correction would each re-fold the month to a
          different number than the one already with the supplier. */}
      <div className={s.panel}>
        <div className={s.panelTitle}>
          R5 — does the world still agree with what we filed?
          {d ? ` — ${d.checked} of ${d.total_filed_months} filed month(s) re-folded` : ''}
        </div>
        <p className={s.panelSub}>
          Each filed month is recomputed from source — impression batches, day verdicts, dated ownership —
          through the same package the issuer used, and compared with the taxable value on the document. This is
          the dear one, so it loads after the checks above rather than holding them up.
        </p>
        {drift.loading && <span className={s.hint}>re-folding…</span>}
        {drift.error && <span className={s.hint}>R5 could not run: {String(drift.error.message || drift.error)}</span>}
        {d && d.rows.map((r) => (
          <div key={`${r.supplier}|${r.period}`} className={s.subjectRow}>
            <Pill tone={r.agrees === true ? 'grow' : r.agrees === false ? 'bad' : 'pend'}>
              {r.agrees === true ? 'agrees' : r.agrees === false ? 'DRIFTED' : 'unanswered'}
            </Pill>
            <span className={s.subjectName}>{r.supplier} · {r.period}</span>
            <span className={s.ownerChip}>
              filed <b>{inr(r.filed_paise)}</b>
              {r.folded_paise != null && <> · re-folds to <b>{inr(r.folded_paise)}</b></>}
              {r.agrees === false && <> · <b>off by {inr(r.delta_paise)}</b></>}
            </span>
            <span className={s.hint}>
              {r.invoice_number}{r.days_judged != null ? ` · ${r.days_judged} days judged` : ''}
              {r.error ? ` · ${r.error}` : ''} · {r.took_ms}ms
            </span>
          </div>
        ))}
        {d && (
          <p className={s.hint} style={{ marginTop: 'var(--s3)' }}>
            {d.uncovered > 0
              ? `${d.uncovered} older filed month(s) were NOT re-folded on this pass — newest first, and the bound is stated rather than silent.`
              : 'Every filed month on record was re-folded on this pass.'}
            {d.unanswered > 0 && ' A fold that failed is shown as unanswered, never as agreement.'}
          </p>
        )}
      </div>

      <div className={s.panel}>
        <div className={s.panelTitle}>The checks</div>
        {checks.map((c) => (
          <div key={c.id} className={s.subjectRow}>
            <span className={s.subjectAxis}>{c.id}</span>
            <span className={s.subjectName} style={{ minWidth: 320 }}>{c.label}</span>
            <Pill tone={c.state === 'breach' ? 'bad' : 'grow'}>{c.state === 'breach' ? 'breach' : 'clean'}</Pill>
            <span className={s.hint}>{c.detail}</span>
          </div>
        ))}
        <p className={s.hint} style={{ marginTop: 'var(--s3)' }}>
          These are the cheap, exact ones — they compare stored things written by different code paths, in SQL, in
          milliseconds. The dearer question, whether a document still equals a live re-fold of its month, needs the
          fold itself and is asked per owner rather than for the whole estate at once.
        </p>
      </div>

      <div className={s.panel}>
        <div className={s.panelTitle}>
          The money cell — day {elapsed == null ? '—' : elapsed}, {met} of {gates.length} gates met
        </div>
        <p className={s.panelSub}>
          The fold re-derives every month from raw impression batches over a window that widens one day per day
          forever, so cost per fleet is driven by platform volume rather than fleet size. The money cell replaces that
          read. It is the riskiest change on this plane — the fold is the source of the money, so a wrong cell is a
          wrong <i>filed</i> tax invoice — which is why the flip waits on all three gates and not on the day count.
        </p>
        {gates.map((g) => (
          <div key={g.id} className={s.subjectRow}>
            <Pill tone={g.met ? 'grow' : 'pend'}>{g.met ? 'met' : 'pending'}</Pill>
            <span className={s.subjectName} style={{ minWidth: 380 }}>{g.label}</span>
            <span className={s.hint}>{g.why}</span>
          </div>
        ))}
      </div>

      <div className={s.panel}>
        <div className={s.panelTitle}>Documents on record — {documents.length}</div>
        <DataTable
          rows={documents}
          rowKey={(r) => r.invoice_number}
          columns={[
            { key: 'invoice_number', label: 'Document', mono: true },
            { key: 'supplier', label: 'Supplier' },
            { key: 'period', label: 'Period', mono: true },
            { key: 'kind', label: 'Kind' },
            { key: 'status', label: 'Status', render: (r) => <Pill tone={r.status === 'issued' ? 'grow' : 'pend'}>{r.status}</Pill> },
            { key: 'taxable_value_paise', label: 'Taxable', align: 'num', render: (r) => inr(r.taxable_value_paise) },
            { key: 'total_paise', label: 'With tax', align: 'num', render: (r) => <strong>{inr(r.total_paise)}</strong> },
            { key: 'day_lines', label: 'Day lines', align: 'num', title: 'The frozen annexure R1 checks the taxable value against.' },
            { key: 'issued_on', label: 'Issued', render: (r) => fmtDay(r.issued_on) },
          ]}
        />
      </div>

      <div className={s.panel}>
        <div className={s.panelTitle}>How far each owner is judged</div>
        <p className={s.panelSub}>
          A month cannot be invoiced until its days are judged through the month’s end — that is the issuer’s own
          gate, so this is what decides which months are even due.
        </p>
        {tenants.map((t) => (
          <div key={t.tenant_id} className={s.subjectRow}>
            <span className={s.subjectName}>{t.name}</span>
            <span className={s.ownerChip}>judged through <b>{fmtDay(t.judged_to)}</b></span>
            <span className={s.hint}>{t.qualified_days} qualified day-verdicts on record</span>
          </div>
        ))}
      </div>

      <Explainer terms={['settled', 'payable', 'statement']} />
    </div>
  );
}
